// ---------------------------------------------------------------------------
// Pure helpers over the FEMA message store shapes (`types/femaMessage.types.ts`).
// Grouping into threads, deriving the display state, and building a legal
// Update/Cancel follow-up all live here so the list, the thread view, the
// action components, and their tests share one reading of the rules in card
// FE-04 ("Traps and rules").
// ---------------------------------------------------------------------------
import type { FemaMessageType } from 'types/fema.types';
import type {
  FemaMessageDisplayState,
  FemaMessageRecord,
  FemaMessageThread,
} from 'types/femaMessage.types';

const FAILURE_RESULT_CLASSES = new Set(['IPAWS_REJECTION', 'NETWORK_FAILURE']);

/** A version failed to reach IPAWS: no send result yet is NOT a failure — it is in flight. */
const isFailedVersion = (version: FemaMessageRecord): boolean =>
  !!version.sendResult && FAILURE_RESULT_CLASSES.has(version.sendResult.resultClass);

export const getRecordIdentifier = (record: FemaMessageRecord): string => record.payload.identifier;
export const getRecordMsgType = (record: FemaMessageRecord): FemaMessageType =>
  record.payload.msgType;
export const getRecordSentAt = (record: FemaMessageRecord): string => record.envelope.sent;
export const getRecordExpires = (record: FemaMessageRecord): string | null =>
  record.payload.info?.[0]?.expires ? String(record.payload.info[0].expires) : null;
export const getRecordHeadline = (record: FemaMessageRecord): string | null =>
  record.payload.info?.[0]?.headline ?? null;
export const getRecordDescription = (record: FemaMessageRecord): string | null =>
  record.payload.info?.[0]?.description ?? null;

/**
 * Groups flat message rows into threads keyed by the original Alert's identifier. A version
 * without a resolvable root (references an identifier not present in `records`) is treated as
 * its own thread root — a message store row should never simply disappear from the list.
 */
export const groupFemaMessagesIntoThreads = (records: FemaMessageRecord[]): FemaMessageThread[] => {
  const byIdentifier = new Map(records.map((record) => [getRecordIdentifier(record), record]));

  const findRootIdentifier = (record: FemaMessageRecord): string => {
    let current = record;
    const seen = new Set<string>();
    while (current.payload.references?.length) {
      const previousIdentifier =
        current.payload.references[current.payload.references.length - 1]?.identifier;
      if (!previousIdentifier || seen.has(previousIdentifier)) break;
      const previous = byIdentifier.get(previousIdentifier);
      if (!previous) break;
      seen.add(previousIdentifier);
      current = previous;
    }
    return getRecordIdentifier(current);
  };

  const threadsByRoot = new Map<string, FemaMessageRecord[]>();
  records.forEach((record) => {
    const rootIdentifier = findRootIdentifier(record);
    const versions = threadsByRoot.get(rootIdentifier) ?? [];
    versions.push(record);
    threadsByRoot.set(rootIdentifier, versions);
  });

  return Array.from(threadsByRoot.entries()).map(([rootIdentifier, versions]) => ({
    rootIdentifier,
    versions: [...versions].sort(
      (a, b) => new Date(getRecordSentAt(a)).getTime() - new Date(getRecordSentAt(b)).getTime(),
    ),
  }));
};

/** The thread's most recent version — the one whose msgType/result decides the display state. */
export const getLatestVersion = (thread: FemaMessageThread): FemaMessageRecord | undefined =>
  thread.versions[thread.versions.length - 1];

/**
 * The display state a row shows (FE-04 acceptance: sent/updated/cancelled/expired/failed).
 * Expiry is a client-side time check, never a server event (a message expiring announces
 * nothing) — it is computed here from `expires` against `now`, and it overrides `sent`/`updated`
 * but never `cancelled` or `failed`.
 */
export const deriveFemaThreadState = (
  thread: FemaMessageThread,
  now: Date = new Date(),
): FemaMessageDisplayState => {
  const latest = getLatestVersion(thread);
  if (!latest) return 'failed';

  if (isFailedVersion(latest)) return 'failed';
  if (getRecordMsgType(latest) === 'Cancel') return 'cancelled';

  const expires = getRecordExpires(latest);
  const isExpired = !!expires && new Date(expires).getTime() <= now.getTime();
  if (isExpired) return 'expired';

  if (getRecordMsgType(latest) === 'Update') return 'updated';
  return 'sent';
};

/** Whether a version reached IPAWS at all — undecided (no result yet) counts as legal to reference. */
export const wasAccepted = (version: FemaMessageRecord): boolean => !isFailedVersion(version);

/**
 * Refuses an illegal Update, with the reason the UI (and the server) gives: there is nothing to
 * reference after a Cancel, a failed send, or an expired message.
 * @returns The reason the action is refused, or null when the Update is legal.
 */
export const getIllegalUpdateReason = (thread: FemaMessageThread): string | null => {
  const latest = getLatestVersion(thread);
  if (!latest) return 'This message has no version to update.';
  if (!wasAccepted(latest)) return 'This message failed to send, so there is nothing to update.';

  const state = deriveFemaThreadState(thread);
  if (state === 'cancelled') return 'This message was cancelled and cannot be updated.';
  if (state === 'expired') return 'This message expired and cannot be updated.';
  return null;
};

/** Refuses an illegal Cancel: the same failed/cancelled/expired states an Update refuses. */
export const getIllegalCancelReason = (thread: FemaMessageThread): string | null => {
  const latest = getLatestVersion(thread);
  if (!latest) return 'This message has no version to cancel.';
  if (!wasAccepted(latest)) return 'This message failed to send, so there is nothing to cancel.';

  const state = deriveFemaThreadState(thread);
  if (state === 'cancelled') return 'This message was already cancelled.';
  if (state === 'expired') return 'This message expired and cannot be cancelled.';
  return null;
};

// `buildFemaFollowupPayload` (the Cancel-via-`POST /v2/fema` follow-up payload builder) and its
// helper `removeBackendOwnedFields` were removed 2026-09-21 (card FE-04 wire-up). `FemaOptions.jsx`
// now calls `cancelFemaAlert` (`POST /v2/fema/:id/cancel`, `services/ipaws/ipaws.api.ts`), which
// sends ONE CAP Cancel for the ONE message it names: earlier and later messages in that message's
// history stay on the air, and an Update puts content back (card BE-28, 2026-09-22). Nothing else
// called either function — see the git history for the removed code.
